import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api, qs } from '../../lib/api';
import { useListState } from '../../lib/hooks';
import { money, plural, WORDS } from '../../lib/format';
import { EmptyState, ErrorState, PageHead, SearchBox, Stat, TableSkeleton } from '../../components/ui';
import { DateRangeFilter } from '../../components/DateRangeFilter';
import { ExportMenu } from '../../components/ExportMenu';
import {
  describeFilters,
  FilterButton,
  FilterChips,
  FilterScope,
  HeaderFilter,
  matchesSearch,
  useLocalFilterTable,
  type FilterDef,
} from '../../components/ColumnFilter';
import { exportNotes, exportPeriod, type TableExportDoc } from '../../lib/tableExport';
import { Icon } from '../../components/Icon';
import { statementPath } from './PartyStatement';

interface BalanceRow {
  id: string;
  name: string;
  /** رصيد ما قبل «من» — صفرٌ حين لا «من». */
  opening: number;
  debit: number;
  credit: number;
  balance: number;
}

type Side = 'receivables' | 'payables';

/** جهةُ الطرف في صفحة كشف الحساب. */
const STATEMENT_SIDE = { receivables: 'customer', payables: 'supplier' } as const;

const SIDES: Record<
  Side,
  {
    title: string;
    subtitle: string;
    label: string;
    party: string;
    empty: string;
    link: (id: string) => string;
    debitLabel: string;
    creditLabel: string;
  }
> = {
  receivables: {
    title: 'أرصدة العملاء',
    subtitle: 'ما لك عند عملائك، من واقع دفتر الأستاذ',
    label: 'مستحق لك',
    party: 'العميل',
    empty: 'لا أرصدة مفتوحة على العملاء. كل ما صدر من فواتير محصَّل بالكامل.',
    link: (id) => `/crm/accounts/${id}`,
    debitLabel: 'إجمالي الفواتير',
    creditLabel: 'إجمالي التحصيل',
  },
  payables: {
    title: 'أرصدة الموردين',
    subtitle: 'ما عليك للموردين، من واقع دفتر الأستاذ',
    label: 'مستحق عليك',
    party: 'المورد',
    empty: 'لا أرصدة مفتوحة للموردين.',
    link: () => '/finance/bills',
    debitLabel: 'إجمالي السداد',
    creditLabel: 'إجمالي الفواتير',
  },
};

/** أعمدة الفلتر — محلّية: الأرصدة تصل كلها في طلب واحد. */
function balanceFilters(side: Side, withOpening: boolean): FilterDef[] {
  const c = SIDES[side];
  return [
    { key: 'name', label: c.party, kind: 'text', sortable: true },
    ...(withOpening ? [{ key: 'opening', label: 'رصيد أول المدة', kind: 'number' as const, sortable: true }] : []),
    { key: 'debit', label: c.debitLabel, kind: 'number', sortable: true },
    { key: 'credit', label: c.creditLabel, kind: 'number', sortable: true },
    { key: 'balance', label: 'الرصيد', kind: 'number', sortable: true },
  ];
}

/**
 * أرصدة الأطراف.
 *
 * الأرقام من دفتر الأستاذ لا من جداول الفواتير. الفارق مهم: الدفتر يشمل
 * القيود اليدوية والتسويات، فيطابق ما يظهر في الميزانية. حساب الرصيد من
 * الفواتير وحدها يعطي رقمًا يبدو صحيحًا ولا يوافق الميزانية، وهو أسوأ من
 * رقم خاطئ ظاهر.
 *
 * ومدى «من/إلى»: رصيد أول المدة كما في «من»، والفواتير والتحصيل حركة المدة
 * وحدها، والرصيد كما في «إلى». بلا مدى: كل الحركة حتى اليوم.
 */
export function Balances({ side }: { side: Side }) {
  const config = SIDES[side];
  const { state, patch } = useListState();
  const [search, setSearch] = useState(state.q ?? '');
  const from = state.from;
  const to = state.to;
  const ranged = Boolean(from || to);
  const defs = useMemo(() => balanceFilters(side, Boolean(from)), [side, from]);

  const list = useQuery({
    queryKey: ['balances', side, from, to],
    queryFn: () => api<BalanceRow[]>(`/accounting/reports/${side}${qs({ from, to })}`),
  });

  const table = useLocalFilterTable({
    rows: list.data,
    defs,
    state,
    patch,
    search: search ? (r: BalanceRow) => matchesSearch(search, r.name) : undefined,
  });
  const rows = table.rows;

  const total = rows.reduce((s, r) => s + r.balance, 0);
  const biggest = rows.reduce<BalanceRow | null>(
    (best, r) => (!best || Math.abs(r.balance) > Math.abs(best.balance) ? r : best),
    null,
  );
  const largest = biggest ? Math.abs(biggest.balance) : 0;

  /* التصدير: الصفوف المعروضة كما هي — بعد البحث والفلاتر والفرز. */
  const buildExport = (): TableExportDoc => ({
    title: config.title,
    sheets: [
      {
        title: config.title,
        period: exportPeriod(from, to),
        sections: [
          {
            columns: [
              { key: 'name', label: config.party },
              ...(from
                ? [{ key: 'opening', label: 'رصيد أول المدة', kind: 'money' as const, total: true }]
                : []),
              { key: 'debit', label: config.debitLabel, kind: 'money', total: true },
              { key: 'credit', label: config.creditLabel, kind: 'money', total: true },
              { key: 'balance', label: 'الرصيد', kind: 'money', total: true },
            ],
            rows: rows.map((r) => ({
              name: r.name,
              opening: r.opening,
              debit: r.debit,
              credit: r.credit,
              balance: r.balance,
            })),
          },
        ],
        notes: exportNotes({
          q: search,
          conditions: describeFilters(defs, table.filters),
          count: rows.length,
          total: rows.length,
          truncated: false,
          unit: 'الأطراف',
        }),
      },
    ],
  });

  return (
    <div className="page">
      <PageHead title={config.title} subtitle={config.subtitle} />

      <div className="stats">
        <Stat
          label={config.label}
          value={<span className="num">{list.data ? money(total) : '—'}</span>}
          note={list.data ? `موزَّع على ${plural(rows.length, WORDS.party)}` : undefined}
        />
        <Stat
          label="أكبر رصيد"
          value={<span className="num">{largest ? money(largest) : '—'}</span>}
          note={biggest?.name}
        />
        <Stat
          label="متوسط الرصيد"
          value={
            <span className="num">{rows.length ? money(total / rows.length) : '—'}</span>
          }
        />
      </div>

      <div className="toolbar sticky-bar">
        <SearchBox
          value={search}
          onChange={(v) => {
            setSearch(v);
            patch({ q: v || undefined });
          }}
          placeholder="بحث بالاسم"
        />
        <DateRangeFilter from={from} to={to} onChange={(f, t) => patch({ from: f, to: t })} />
        <ExportMenu build={buildExport} disabled={!rows.length} />
        <span className="row" style={{ marginInlineStart: 'auto', gap: 'var(--sp-2)' }}>
          <FilterButton {...table.button} />
        </span>
      </div>

      <FilterChips {...table.chips} />

      {list.isError && <ErrorState error={list.error} onRetry={() => void list.refetch()} />}

      <FilterScope {...table.scope}>
        <div className="card">
          <div className="table-wrap sticky-head">
            <table className="table">
              <thead>
                <tr>
                  <th>
                    {config.party}
                    <HeaderFilter colKey="name" />
                  </th>
                  {from && (
                    <th className="col-num" style={{ width: 150 }}>
                      رصيد أول المدة
                      <HeaderFilter colKey="opening" />
                    </th>
                  )}
                  <th className="col-num" style={{ width: 150 }}>
                    {config.debitLabel}
                    <HeaderFilter colKey="debit" />
                  </th>
                  <th className="col-num" style={{ width: 150 }}>
                    {config.creditLabel}
                    <HeaderFilter colKey="credit" />
                  </th>
                  <th className="col-num" style={{ width: 150 }}>
                    {to ? 'الرصيد في آخر المدة' : 'الرصيد'}
                    <HeaderFilter colKey="balance" />
                  </th>
                  <th style={{ width: 200 }}>الوزن</th>
                  <th style={{ width: 48 }} />
                </tr>
              </thead>

              {list.isPending && <TableSkeleton cols={from ? 7 : 6} />}

              {list.data && (
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id}>
                      <td className="cell-strong">
                        <Link to={config.link(r.id)} className="link">
                          {r.name}
                        </Link>
                      </td>
                      {from && <td className={`num ${r.opening < 0 ? 'neg' : 'cell-mute'}`}>{money(r.opening)}</td>}
                      <td className="num cell-mute">{money(r.debit)}</td>
                      <td className="num cell-mute">{money(r.credit)}</td>
                      {/* الرصيدُ السالب (دائنٌ للعميل / مدينٌ على المورد) بالأحمر كما في الكشف. */}
                      <td className={`num cell-strong${r.balance < 0 ? ' neg' : ''}`}>{money(r.balance)}</td>
                      <td>
                        <div className="bar-track">
                          <div
                            className="bar-fill"
                            style={{
                              width: largest ? `${(Math.abs(r.balance) / largest) * 100}%` : '0%',
                            }}
                          />
                        </div>
                      </td>
                      <td className="end">
                        {/* كشفُ الطرف بمدة الصفحة نفسها — وبلا مدى فبمدته الافتراضية. */}
                        <Link
                          to={statementPath(STATEMENT_SIDE[side], r.id, { from, to })}
                          className="btn btn-sm btn-icon btn-ghost"
                          title={`كشف حساب ${r.name}`}
                          aria-label={`كشف حساب ${r.name}`}
                        >
                          <Icon name="receipt" size={15} />
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              )}
            </table>
          </div>

          {list.data && rows.length === 0 && (
            <EmptyState
              title="لا أرصدة"
              text={
                search || table.active
                  ? 'لا نتائج لهذا البحث أو الفلتر.'
                  : ranged
                    ? 'لا رصيد ولا حركة في هذه المدة.'
                    : config.empty
              }
            />
          )}
        </div>
      </FilterScope>
    </div>
  );
}
